/**
 * Курсы подачи: очередь волн на кухне.
 *
 * Смысл в том, что горячее не должно стынуть, пока гость ест салат.
 * Официант помечает позиции курсами, кухня получает их волнами:
 * первый — сразу, следующие — с задержкой, чтобы блюдо вышло
 * к моменту, когда предыдущее доедено.
 */

/** 0 — VIP (вперёд всех), 1..4 — обычные волны. */
export type Course = 0 | 1 | 2 | 3 | 4;

export const COURSE_NAMES: Record<Course, string> = {
  0: 'Срочно',
  1: 'Первая подача',
  2: 'Вторая подача',
  3: 'Третья подача',
  4: 'Десерт',
};

export type CourseSettings = {
  enabled: boolean;
  /** задержка старта в минутах по номеру курса */
  delays: Partial<Record<Course, number>>;
};

export const DEFAULT_COURSES: CourseSettings = {
  enabled: false,
  delays: { 2: 12, 3: 25, 4: 35 },
};

/**
 * Задержка курса. Ноль и первый готовятся немедленно — это не настройка,
 * а правило: гость сел и ждёт, первая волна не может опаздывать.
 */
export function courseDelayMin(course: Course, s: CourseSettings): number {
  if (course <= 1) return 0;
  return Math.max(0, s.delays[course] ?? 0);
}

/** Пора ли начинать курс: отсчёт идёт от отправки заказа на кухню. */
export function courseReadyToStart(
  course: Course,
  firedAt: Date,
  now: Date,
  s: CourseSettings,
): boolean {
  const delay = courseDelayMin(course, s);
  if (delay === 0) return true;
  return now.getTime() - firedAt.getTime() >= delay * 60_000;
}

/** Сколько минут ещё ждать курсу; 0 — уже можно готовить. */
export function minutesUntilStart(
  course: Course,
  firedAt: Date,
  now: Date,
  s: CourseSettings,
): number {
  const delay = courseDelayMin(course, s);
  if (delay === 0) return 0;
  const left = delay * 60_000 - (now.getTime() - firedAt.getTime());
  return left <= 0 ? 0 : Math.ceil(left / 60_000);
}

/** Группировка позиций по курсам — то, что рисует кухонный экран. */
export function groupByCourse<T extends { course?: number | null }>(
  items: T[],
): { course: Course; items: T[] }[] {
  const by = new Map<Course, T[]>();
  for (const it of items) {
    const c = normalizeCourse(it.course);
    by.set(c, [...(by.get(c) ?? []), it]);
  }
  return [...by.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([course, list]) => ({ course, items: list }));
}

/** Чужие и кривые значения приводим к первой подаче, а не роняем экран. */
export function normalizeCourse(v: number | null | undefined): Course {
  if (v === null || v === undefined) return 1;
  const n = Math.trunc(v);
  return (n >= 0 && n <= 4 ? n : 1) as Course;
}

/**
 * Можно ли перевести весь курс одной кнопкой: только если каждая позиция
 * готова к переходу. Половина курса в другом статусе — признак,
 * что повар ведёт позиции по одной, и групповая кнопка тут навредит.
 */
export function courseCanAdvance<T extends { status: string }>(
  items: T[],
  from: string,
): boolean {
  return items.length > 0 && items.every((i) => i.status === from);
}
